import { BusinessObjectBase } from './base';
import type { Candidate } from './candidate';
import type { InterviewMotivation } from './interview-motivation';
import type { InterviewObservation } from './interview-observation';
import type { InterviewProblemSolving } from './interview-problem-solving';
import type { InterviewStressResistance } from './interview-stress-resistance';
import type { InterviewTeamwork } from './interview-teamwork';
import type { User } from './user';

export interface InterviewData {
  readonly id: number;
  readonly interviewDate?: string;
  readonly candidateId: number;
  readonly candidate?: Candidate;
  readonly interviewerId: number;
  readonly interviewer?: User;
  readonly interviewMotivation?: InterviewMotivation;
  readonly interviewTeamwork?: InterviewTeamwork;
  readonly interviewProblemSolving?: InterviewProblemSolving;
  readonly interviewStressResistance?: InterviewStressResistance;
  readonly interviewObservation?: InterviewObservation;
}

export interface Interview extends Readonly<InterviewData> {}
export class Interview extends BusinessObjectBase {
  static readonly entityName = 'Interview' as const;
  static readonly plural = 'interviews';

  // --- Static CRUD methods ---

  static async find(filter?: any): Promise<Interview[]> {
    const url = `${this.baseUrl}/${this.plural}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const results = await this.get<any[]>(url, params);
    return results.map((data) => new Interview(data));
  }

  static async findOne(filter?: any): Promise<Interview | null> {
    const url = `${this.baseUrl}/${this.plural}/find-one`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return data ? new Interview(data) : null;
  }

  static async findById(id: number, filter?: any): Promise<Interview> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const params: Record<string, string> = {};
    if (filter) params['filter'] = JSON.stringify(filter);
    const data = await this.get<any>(url, params);
    return new Interview(data);
  }

  static async create(data: Omit<InterviewData, 'id'>): Promise<Interview> {
    const url = `${this.baseUrl}/${this.plural}`;
    const result = await this.post<any>(url, data);
    return new Interview(result);
  }

  static async updateById(
    id: number,
    data: Partial<InterviewData>,
  ): Promise<Interview> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    const result = await this.patch<any>(url, data);
    return new Interview(result);
  }

  static async deleteById(id: number): Promise<boolean> {
    const url = `${this.baseUrl}/${this.plural}/${id}`;
    try {
      await this.del<void>(url);
      return true;
    } catch {
      return false;
    }
  }
}
